'use client'

import React, { ReactElement, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { SubmitButton } from '@/components/Buttons/Buttons'
import { login, LoginResponse } from '../_actions/login'
import { loginSchema, type LoginFormData } from '@/lib/validations/auth'
import Link from 'next/link'
import { BrandWordmark } from '@/components/ui/BrandWordmark'

export default function LoginForm(): ReactElement {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const router = useRouter()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  })

  const onSubmit = async (data: LoginFormData) => {
    if (isLoading) return
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    const result: LoginResponse = await login(data)

    if (result.success) {
      setSuccess('Login successful. Redirecting...')
      let to = '/dashboard'
      try {
        if (typeof window !== 'undefined') {
          const sp = new URLSearchParams(window.location.search)
          const qp = sp.get('return')
          if (qp && qp.startsWith('/')) to = decodeURIComponent(qp)
          else {
            const hash = window.location.hash
            if (hash && hash.length > 1) {
              const dest = decodeURIComponent(hash.slice(1))
              if (dest.startsWith('/')) to = dest
            }
          }
        }
      } catch {}
      router.push(to)
      return
    }

    setIsLoading(false)
    setError(result.error || null)
  }

  return (
    <div className="flex min-h-full flex-col justify-center items-center">
      <header className="text-xl md:text-2xl font-bold flex items-center gap-2">
        <BrandWordmark /> Login
      </header>
      <div className="w-full mx-auto md:max-w-sm">
        <form className="flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col">
            <label htmlFor="email">Email</label>
            <input
              className={`textInput placeholder-white/50 ${errors.email ? 'border-red-500' : ''}`}
              type="email"
              id="email"
              placeholder="Enter your email"
              {...register('email')}
              disabled={isLoading}
            />
            {errors.email && (
              <span className="text-red-500 text-sm mt-1">{errors.email.message}</span>
            )}
          </div>
          <div className="flex flex-col">
            <label htmlFor="password">Password</label>
            <input
              className={`textInput placeholder-white/50 ${
                errors.password ? 'border-red-500' : ''
              }`}
              type="password"
              id="password"
              placeholder="Enter your password"
              {...register('password')}
              disabled={isLoading}
            />
            {errors.password && (
              <span className="text-red-500 text-sm mt-1">{errors.password.message}</span>
            )}
          </div>
          {error && <div className="text-red-600">{error}</div>}
          {success && <div className="text-green-500">{success}</div>}
          <SubmitButton className="mb-5" loading={isLoading} text="Login" color="primary" />
        </form>
        <footer className="text-sm">
          <div className="flex justify-center gap-2">
            <Link href="/forgot-password">Forgot Password?</Link>
            {/* <span>|</span>
            <Link href="/signup">Sign up</Link> */}
          </div>
        </footer>
      </div>
    </div>
  )
}
